import { formatCents, formatCount, parseMoneyToCents, tt } from '@agos/shared'

import {
  allocatedPct,
  allocationNote,
  housePct,
  isOverAllocated,
  type FranchiseShare,
} from '@agos/saas-franchises'

/**
 * What each franchise earns the platform, and what each partner's share of it comes to.
 *
 * **FB-S01 — a margin needs revenue to be a margin of.** The server divides profit by revenue, and
 * Sereď's revenue is 0,00 €, so `marginPct` comes back `0.0` while the profit is **−30,46 €**. Legacy
 * prints it: `{r.marginPct.toFixed(1)} %`. A row reading −30,46 € and 0,0 % side by side says the
 * franchise broke even while losing thirty euros. Here the margin is shown only where there is revenue.
 *
 * **FB-S02 — a negative payout is not money received.** Legacy colours every payout
 * `text-emerald-300` unconditionally, so a partner's share of a loss is drawn in the colour that means
 * *paid to you*. `CardPay.tsx` gets the same case right four times over. The sign drives the emphasis
 * here, and the loss is said in words as well.
 *
 * The window ends at *now*, so the totals drift between two calls seconds apart — 168,57 € and 168,60 €
 * were both observed. Every figure is read from the response; none is recomputed or pinned.
 */

export interface PartnerSplit {
  partnerId: number
  partnerName: string
  sharePct: number
  payoutCents: number
}

export interface FranchiseProfit {
  franchiseId: number
  franchiseName: string
  franchiseSlug: string
  revenueCents: number
  totalCostCents: number
  profitCents: number
  /** `null` when there is no revenue to compute a margin from — FB-S01. */
  marginPct: number | null
  partnerSplit: PartnerSplit[]
  /**
   * The three components of `totalCostCents` - `SAAS.md` §3.41.
   *
   * **The endpoint has always answered these and the screen showed only the total.** Legacy puts two
   * of the three in a `title` tooltip and omits `costModules` entirely; §3.10 found 12 368 of
   * franchise 1's 111 251 metered calls unpriced, and §3.37 has just given the owner screens to set
   * the hardware and module charges - so *is our cost the API or the hardware* is the question the
   * total cannot answer.
   */
  costApiCents: number
  costHardwareCents: number
  costModulesCents: number
}

/** One partner's payout across every franchise they hold a share in - §3.41. */
export interface PartnerTotal {
  partnerId: number
  partnerName: string
  payoutCents: number
  /**
   * How many franchises it is a sum over, so one row does not read like one share.
   *
   * **Counted from `perFranchise`, not read off the row** - the endpoint's `perPartner` entries carry
   * `partnerId`, `partnerName` and `payout` and nothing else, checked against the service rather
   * than assumed. The splits are already in the same payload, so this costs no request and no
   * endpoint change.
   */
  franchises: number
}

/** One day of the report window - §3.41. */
export interface DailyCost {
  day: string
  variableCents: number
  recurringCents: number
  totalCents: number
}

export interface ProfitabilityReport {
  totals: { revenueCents: number; totalCostCents: number; profitCents: number; housePayoutCents: number }
  perFranchise: FranchiseProfit[]
  /** The server's own aggregate, which the screen made the operator sum by hand. */
  perPartner: PartnerTotal[]
  dailyCost: DailyCost[]
  /** `window.from` / `window.to`, also unread: a series with no dates is a shape, not a figure. */
  window: { from: string; to: string }
}

function num(value: unknown): number {
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

export function profitabilityOf(payload: unknown): ProfitabilityReport {
  const row = (typeof payload === 'object' && payload !== null ? payload : {}) as Record<string, unknown>
  const totals = (typeof row.totals === 'object' && row.totals !== null ? row.totals : {}) as Record<string, unknown>
  const franchises = Array.isArray(row.perFranchise) ? row.perFranchise : []

  // Built first, because §3.41's partner totals count the franchises each partner appears in and the
  // splits that answer that are in here.
  const perFranchise: FranchiseProfit[] = franchises.map((entry) => {
      const franchise = (typeof entry === 'object' && entry !== null ? entry : {}) as Record<string, unknown>
      const revenueCents = parseMoneyToCents(franchise.revenue as string | number | null)
      const splits = Array.isArray(franchise.partnerSplit) ? franchise.partnerSplit : []
      return {
        franchiseId: num(franchise.franchiseId),
        franchiseName: typeof franchise.franchiseName === 'string' ? franchise.franchiseName : '',
        franchiseSlug: typeof franchise.franchiseSlug === 'string' ? franchise.franchiseSlug : '',
        revenueCents,
        totalCostCents: parseMoneyToCents(franchise.totalCost as string | number | null),
        profitCents: parseMoneyToCents(franchise.profit as string | number | null),
        // FB-S01: the server's zero-on-no-revenue is discarded rather than displayed.
        marginPct: revenueCents > 0 ? num(franchise.marginPct) : null,
        costApiCents: parseMoneyToCents(franchise.costApi as string | number | null),
        costHardwareCents: parseMoneyToCents(franchise.costHw as string | number | null),
        costModulesCents: parseMoneyToCents(franchise.costModules as string | number | null),
        partnerSplit: splits.map((split) => {
          const partner = (typeof split === 'object' && split !== null ? split : {}) as Record<string, unknown>
          return {
            partnerId: num(partner.partnerId),
            partnerName: typeof partner.partnerName === 'string' ? partner.partnerName : '',
            sharePct: num(partner.sharePct),
            payoutCents: parseMoneyToCents(partner.payout as string | number | null),
          }
        }),
      }
  })

  return {
    totals: {
      revenueCents: parseMoneyToCents(totals.revenue as string | number | null),
      totalCostCents: parseMoneyToCents(totals.totalCost as string | number | null),
      profitCents: parseMoneyToCents(totals.profit as string | number | null),
      housePayoutCents: parseMoneyToCents(totals.housePayout as string | number | null),
    },
    perFranchise,
    perPartner: partnerTotalsOf(row.perPartner, perFranchise),
    dailyCost: dailyCostSeries(row.dailyCost),
    window: windowOf(row.window),
  }
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function windowOf(value: unknown): { from: string; to: string } {
  const row = (typeof value === 'object' && value !== null ? value : {}) as Record<string, unknown>
  return { from: text(row.from), to: text(row.to) }
}

/**
 * One row per partner, summed across their franchises - §3.41.
 *
 * **The screen renders `partnerSplit` per franchise**, so a partner holding shares in three
 * franchises appears three times and *what do we owe them* is a sum done by eye; both of the owner's
 * partners hold shares in all three. The server already answers this and nothing read it.
 *
 * The franchise count rides along because a single row reading 240,00 € would otherwise look like one
 * share rather than three.
 */
export function partnerTotalsOf(
  value: unknown,
  perFranchise: readonly FranchiseProfit[] = [],
): PartnerTotal[] {
  if (!Array.isArray(value)) return []
  // How many franchises each partner appears in, from the splits already in this payload.
  const seen = new Map<number, number>()
  for (const franchise of perFranchise) {
    for (const split of franchise.partnerSplit) {
      seen.set(split.partnerId, (seen.get(split.partnerId) ?? 0) + 1)
    }
  }
  return (value as Record<string, unknown>[]).flatMap((row) =>
    typeof row.partnerId === 'number'
      ? [
          {
            partnerId: row.partnerId,
            partnerName: text(row.partnerName),
            // **The server's own sum, not a re-derivation.** The splits are here and adding them up
            // would be a second implementation of the same arithmetic, free to disagree with the
            // first - which is a defect this repository has recorded more than once.
            payoutCents: parseMoneyToCents(row.payout as string | number | null),
            franchises: seen.get(row.partnerId) ?? 0,
          },
        ]
      : [],
  )
}

/**
 * The per-day cost series - §3.41.
 *
 * **Live data, checked before it was worth building**: `api_usage_log` runs 2026-05-11 to 2026-09-18,
 * 112 238 calls across 83 days, because AGOS writes it itself rather than inheriting it from the
 * dump.
 */
export function dailyCostSeries(value: unknown): DailyCost[] {
  if (!Array.isArray(value)) return []
  return (value as Record<string, unknown>[]).flatMap((row) =>
    typeof row.day === 'string' && row.day !== ''
      ? [
          {
            day: row.day,
            variableCents: parseMoneyToCents(row.variable as string | number | null),
            recurringCents: parseMoneyToCents(row.recurring as string | number | null),
            totalCents: parseMoneyToCents(row.total as string | number | null),
          },
        ]
      : [],
  )
}

/** The three components under the total, all three of them - §3.41. */
export function costBreakdownOf(
  franchise: FranchiseProfit,
): { label: string; cents: number }[] {
  return [
    { label: tt('API'), cents: franchise.costApiCents },
    { label: tt('Hardvér'), cents: franchise.costHardwareCents },
    { label: tt('Moduly'), cents: franchise.costModulesCents },
  ]
}

/**
 * What the three components add up to, against what the server called the total.
 *
 * **The control that a missing-key census cannot give you.** Reading `costHw` where the server sends
 * `costHardware`, or the other way round, produces three plausible figures that do not reconcile -
 * and one of them silently nought. If this disagrees with `totalCostCents`, a key is mis-mapped.
 */
export function breakdownSumCents(franchise: FranchiseProfit): number {
  return franchise.costApiCents + franchise.costHardwareCents + franchise.costModulesCents
}

/** Totals for the series, which is legacy's own summary line and the finding inside the chart. */
export function dailyCostTotals(series: readonly DailyCost[]): {
  totalCents: number
  variableCents: number
  recurringCents: number
  peakCents: number
} {
  return {
    totalCents: series.reduce((sum, day) => sum + day.totalCents, 0),
    variableCents: series.reduce((sum, day) => sum + day.variableCents, 0),
    recurringCents: series.reduce((sum, day) => sum + day.recurringCents, 0),
    peakCents: series.reduce((peak, day) => Math.max(peak, day.totalCents), 0),
  }
}

/**
 * A bar's height as a percentage of the window's peak.
 *
 * **No charting dependency** - there is none in any `package.json` and that is deliberate;
 * `StatisticsPage` draws its day-hour heat grid with divs and inline styles, and this is the same.
 * An empty window answers 0 rather than dividing by nought.
 */
export function dailyCostHeight(day: DailyCost, peakCents: number): number {
  if (peakCents <= 0) return 0
  return Math.round((day.totalCents / peakCents) * 100)
}

/** `85,6 %`, or a stated absence — never `0,0 %` on a franchise with no revenue. */
export function marginNote(franchise: FranchiseProfit): string {
  if (franchise.marginPct === null) return tt('Bez tržieb')
  return `${formatCount(Math.round(franchise.marginPct * 10) / 10)} %`
}

/**
 * How the per-franchise rows may be ordered - `SAAS.md §3.112`.
 *
 * **Legacy's five sortable columns, kept as five keys.** Its table header toggles on `franchiseName`,
 * `revenue`, `totalCost`, `profit` and `marginPct`; this screen draws cards rather than a table, so the
 * control is a list of keys instead of a row of headers - but the question is the same one and it was
 * unanswerable here: *which franchise is losing us money* against a page that lists them in whatever
 * order the server sent.
 */
export const PROFIT_SORTS = [
  { key: 'profit', label: tt('Zisk') },
  { key: 'revenue', label: tt('Tržby') },
  { key: 'cost', label: tt('Náklady') },
  { key: 'margin', label: tt('Marža') },
  { key: 'name', label: tt('Názov') },
] as const

export type ProfitSortKey = (typeof PROFIT_SORTS)[number]['key']

/**
 * The figure a key sorts on, or `null` when the row has none.
 *
 * **`margin` is the one that can be absent**, and FB-S01 is why: a franchise with no revenue has no
 * margin to compute, and this screen already refuses to print one for it. Sorting such a row as a
 * **0 %** margin would put it beside a franchise that genuinely broke even, which is the same
 * conflation that entry exists to stop.
 */
function sortFigure(franchise: FranchiseProfit, key: ProfitSortKey): number | null {
  switch (key) {
    case 'profit':
      return franchise.profitCents
    case 'revenue':
      return franchise.revenueCents
    case 'cost':
      return franchise.totalCostCents
    case 'margin':
      return franchise.marginPct
    case 'name':
      return null
  }
}

/**
 * The rows in the order asked for - §3.112.
 *
 * **A copy, never in place.** `report.perFranchise` comes out of a `useMemo` over the query's data, so
 * sorting it where it lies would mutate what React Query holds and the order would then depend on how
 * many times the component had rendered.
 *
 * **A row with no figure sorts last in both directions.** Not *smallest*: a franchise whose margin
 * cannot be computed is not the worst-performing one, and putting it at the top of an ascending sort
 * would say exactly that. The same rule `StatsPage`'s `sortValue: null` follows, for the same reason.
 *
 * Ties break on the name, so the list does not move between loads - two franchises at 0,00 € profit
 * would otherwise sit in whichever order the server sent.
 */
export function sortedFranchises(
  rows: readonly FranchiseProfit[],
  key: ProfitSortKey,
  descending: boolean,
): FranchiseProfit[] {
  const byName = (left: FranchiseProfit, right: FranchiseProfit) =>
    left.franchiseName.localeCompare(right.franchiseName, 'sk')

  return [...rows].sort((left, right) => {
    if (key === 'name') {
      return descending ? byName(right, left) : byName(left, right)
    }
    const first = sortFigure(left, key)
    const second = sortFigure(right, key)
    if (first === null && second === null) return byName(left, right)
    if (first === null) return 1
    if (second === null) return -1
    if (first === second) return byName(left, right)
    return descending ? second - first : first - second
  })
}

export function isLoss(franchise: FranchiseProfit): boolean {
  return franchise.profitCents < 0
}

/** FB-S02: a share of a loss is not a payout, and green is the wrong colour for it. */
export function isCharge(split: PartnerSplit): boolean {
  return split.payoutCents < 0
}

export function chargeNote(split: PartnerSplit): string {
  if (!isCharge(split)) return ''
  return `${tt('Podiel na strate')}: ${formatCents(Math.abs(split.payoutCents))}`
}

/**
 * Whether the shares add up to the whole, which is what makes `housePayout` zero.
 *
 * Measured: 30/70, 50/50 and 70/30 - each summing to 100, and `housePayout: 0.00`. A set that does
 * not sum to 100 is not an error here, it is the house's cut; it is worth naming so a typo in one
 * share does not read as a policy.
 *
 * **Routed through `allocatedPct` rather than summing again** - §3.9's implementation, with the
 * rounding that stops three shares of 33,33 adding to 99,98999999999999.
 */
export function sharesSumPct(franchise: FranchiseProfit): number {
  return allocatedPct(asShares(franchise))
}

/**
 * What the platform keeps - **and negative when the shares exceed the profit.**
 *
 * **This clamped, and that was the server's bug reproduced on the client.** It read
 * `Math.max(0, 100 - sharesSumPct(franchise))`, exactly as `ProfitabilityService` reports
 * `housePct = Math.max(0d, 100d - sharedTotalPct)`. SAAS.md §3.9 fixed the arithmetic on the
 * partners screen and named only the server - and the copy it fixed is what hid this one, which is
 * the page FB-505's own complaint is about.
 *
 * **On this page the clamp was worse than a wrong figure**, because `ProfitabilityPage` renders the
 * line only `if (houseSharePct(franchise) > 0)`: an over-allocation and a correct 100 % both drew
 * **nothing at all**, so the two were indistinguishable. `houseShareNote` is what the page reads
 * now.
 */
export function houseSharePct(franchise: FranchiseProfit): number {
  return housePct(asShares(franchise))
}

/**
 * The allocation in words, said for every franchise that has partners at all.
 *
 * The same sentence §3.9's partners screen uses, from the same function - the two pages describe one
 * arrangement and must not word it differently.
 */
export function houseShareNote(franchise: FranchiseProfit): string {
  if (franchise.partnerSplit.length === 0) return ''
  return allocationNote(asShares(franchise))
}

export function isOverAllocatedProfit(franchise: FranchiseProfit): boolean {
  return isOverAllocated(asShares(franchise))
}

/**
 * A profit row's splits as §3.9's shares.
 *
 * `linkId` is null because this payload has none: `GET …/profitability` reports what was paid, not
 * the `franchise_partners` row it came from, and nothing on this page removes a share.
 */
function asShares(franchise: FranchiseProfit): FranchiseShare[] {
  return franchise.partnerSplit.map((split) => ({
    linkId: null,
    partnerId: split.partnerId,
    partnerName: split.partnerName,
    sharePct: split.sharePct,
  }))
}
